/**
 * Per-tenant AI allowance.
 *
 * WHY THIS EXISTS SEPARATELY FROM CREDITS
 * --------------------------------------
 * Pro's credit allotment is 100,000 and an intelligence call costs 3, so the
 * credit pool alone would permit 33,333 model calls a month. At the measured
 * ~$0.0058 per call that is ~$193 of Bedrock on a $49 plan. Credits meter API
 * work; this meters model spend, and the two must not be the same pool.
 *
 * RESERVE BEFORE THE CALL, NEVER REFUND
 * -------------------------------------
 * The allowance is consumed before the model runs. Charging on success would
 * make every error path free, and anyone able to induce an error would have
 * unmetered Bedrock on this account. A rare wasted call costs half a cent.
 *
 * The key is per-`sub`. One tenant can never spend another's allowance, and
 * nothing here is cached at module scope: a Worker isolate is reused across
 * tenants, so module-scope state is shared between them.
 */

/**
 * Anything with KV's get/put shape. Every Worker passes its `CARRIER_USERS`.
 *
 * `get` is typed as returning a string because that is what Workers KV returns
 * for a one-argument call. It is declared `unknown` at the value position
 * anyway: callers in this repo also hold KV handles whose `get` was written to
 * parse JSON itself (`get(key, "json")`, and every in-memory test double built
 * to imitate it), and those hand back an object. Reading a counter that came
 * back as an object as if it were a string is how a meter silently reports
 * zero and hands out a second allowance, so `readCount` accepts both.
 */
export interface AiBudgetStore {
  get(key: string): Promise<unknown>;
  put(key: string, value: string, options?: { expirationTtl?: number }): Promise<void>;
}

export type AiTier = "free" | "pro" | "enterprise" | "superadmin";

/** Window a tier's allowance resets on. Free resets daily, paid monthly. */
export type AiWindow = "day" | "month";

export interface AiTierPolicy {
  limit: number;
  window: AiWindow;
}

/**
 * Included model calls per tier.
 *
 * Free is deliberately a taste, not a trial: 5/day is enough to see that the
 * assistant answers from real fleet data and not enough to be a cost centre.
 * Pro's 1,000/month is ~$5.80 of Bedrock against $49, sized from billing.ts's
 * measured ~$0.0058/call so the plan cannot lose money on AI.
 */
export const AI_TIER_POLICY: Record<AiTier, AiTierPolicy> = {
  free: { limit: 5, window: "day" },
  pro: { limit: 1_000, window: "month" },
  enterprise: { limit: 10_000, window: "month" },
  // Finite, deliberately. This was `Infinity` when the tier was introduced in
  // #762, and Infinity breaks in two ways that both hide themselves:
  //
  //   1. JSON.stringify(Infinity) is "null". The quota endpoint returned
  //      {"limit":null,"remaining":null} and the console badge rendered
  //      "1 of null used". That is the whole reason this was investigated.
  //   2. An infinite allowance is unmetered Bedrock. A staff account could
  //      spend without bound and nothing would refuse it or show a number.
  //
  // 100,000 is roughly $580 of Haiku at the measured $0.0058/call. Nobody on
  // staff will reach it by using the product, and a runaway loop stops.
  superadmin: { limit: 100_000, window: "month" },
};

/**
 * Every limit must survive JSON and must actually bound spend.
 *
 * Asserted at module load rather than only in a test, because the failure mode
 * is silent: an infinite or NaN limit serialises to null, the UI shows nothing,
 * and the meter never refuses. A crash on deploy is much easier to notice.
 */
for (const [tier, policy] of Object.entries(AI_TIER_POLICY)) {
  if (!Number.isFinite(policy.limit) || policy.limit <= 0) {
    throw new Error(
      `AI_TIER_POLICY.${tier}.limit must be a positive finite number, got ${policy.limit}. ` +
        `A non-finite limit serialises to null in JSON and leaves spend unmetered.`,
    );
  }
}

/**
 * Resolve a tier that came from storage rather than from the type system.
 *
 * `AI_TIER_POLICY[tier]` looks total because `AiTier` is a union, and it is
 * not: the tier arrives from a KV record and is cast on the way in. Production
 * holds `org:<platform org>:tier = superadmin`, a value the `Tier` union says
 * cannot exist. Indexing on it returned undefined, `limit` and `remaining`
 * serialised as null, the quota badge rendered "1 of null used", and the chat
 * route threw where it read `policy.limit`.
 *
 * TypeScript could not catch it. The value never appears in source: it is data.
 *
 * Unknown tiers resolve to ENTERPRISE, not free. A Carrier staff account is the
 * realistic source of an unrecognised tier, and refusing staff their own
 * product is a worse outcome than granting a generous, still-bounded allowance.
 * The important word is bounded: the previous behaviour was not a bigger
 * allowance, it was a crash and a null.
 */
export function policyForTier(tier: string): AiTierPolicy {
  return AI_TIER_POLICY[tier as AiTier] ?? AI_TIER_POLICY.enterprise;
}

/** True when the tier is one this module actually models. */
export function isKnownTier(tier: string): tier is AiTier {
  return tier in AI_TIER_POLICY;
}

/** UTC period stamp. UTC, not local: a tenant must not get two resets by flying west. */
export function periodStamp(window: AiWindow, now: Date): string {
  const y = now.getUTCFullYear();
  const m = String(now.getUTCMonth() + 1).padStart(2, "0");
  if (window === "month") return `${y}${m}`;
  return `${y}${m}${String(now.getUTCDate()).padStart(2, "0")}`;
}

/** Per-tenant budget key. Never shared across `sub`. */
export function aiBudgetKey(sub: string, window: AiWindow, stamp: string): string {
  return `ai_usage:${sub}:${window}:${stamp}`;
}

/**
 * Keys that used to hold a slice of this same allowance, still being read.
 *
 * THE BUG THIS EXISTS TO CLOSE
 * ----------------------------
 * One allowance was being metered on three keys in one KV namespace, so a Pro
 * tenant got the full 1,000 calls on each: `ask_usage:` from carrier_ask in the
 * MCP Worker, `ai_usage:<sub>:<yyyymm>` from the MCP intelligence composites,
 * and this module's key from the REST API and the console. Nothing errored.
 * Both old meters counted correctly, on keys nobody else read. At ~$0.0058 a
 * call that is roughly $17 of Bedrock against a $49 plan sized for $5.80.
 *
 * WHY READ THEM AT ALL
 * --------------------
 * Those keys hold real production data: carrier_ask has been live. Switching to
 * the new key alone would zero every tenant mid-month and hand out a second
 * full allowance to exactly the tenants who had already spent theirs, which is
 * the same money bug wearing a cutover as a disguise.
 *
 * SUM, NOT MAX
 * ------------
 * The counters partition the month in time: the old keys hold what a tenant
 * spent before this deploy, the new key what it spends after. Their sum is the
 * true month-to-date figure. `max` would forgive the smaller side, a tenant on
 * 900 legacy and 150 new reads as 900, so it gets 250 free calls for having
 * used both surfaces, which is the bug restated.
 *
 * WHEN THIS CAN GO
 * ----------------
 * Nothing writes these keys after this change. Both carry their own TTL, 35
 * days for the monthly keys and 2 days for the daily one, so every one of them
 * is expired 35 days after this ships and a read can only return null. Delete
 * this function and its call site any time after 2026-10-01 (ship date
 * 2026-08-22 plus the 35-day TTL plus slack). The check that it is safe is that
 * `git log -S "ask_usage:"` shows no writer, not that the code looks unused.
 */
export function legacyAiBudgetKeys(sub: string, window: AiWindow, stamp: string): string[] {
  if (window === "month") {
    return [
      // apps/mcp-server/src/billing.ts, carrier_ask Pro monthly.
      `ask_usage:${sub}:${stamp}`,
      // apps/mcp-server/src/intelligence-model.ts, the composites' own monthly key.
      `ai_usage:${sub}:${stamp}`,
    ];
  }
  // apps/mcp-server/src/billing.ts, carrier_ask free daily.
  return [`ask_daily:${sub}:${stamp}`];
}

export interface AiUsage {
  used: number;
  limit: number;
  remaining: number;
  window: AiWindow;
  /** ISO instant the allowance next resets. */
  resetsAt: string;
}

function resetsAt(window: AiWindow, now: Date): string {
  const d = new Date(now);
  if (window === "day") {
    d.setUTCHours(0, 0, 0, 0);
    d.setUTCDate(d.getUTCDate() + 1);
  } else {
    d.setUTCHours(0, 0, 0, 0);
    d.setUTCDate(1);
    d.setUTCMonth(d.getUTCMonth() + 1);
  }
  return d.toISOString();
}

function callsOf(value: unknown): number {
  if (value === null || value === undefined) return 0;
  let parsed: unknown = value;
  if (typeof value === "string") {
    try {
      parsed = JSON.parse(value);
    } catch {
      return 0;
    }
  }
  if (typeof parsed !== "object" || parsed === null) return 0;
  const n = Number((parsed as { calls?: unknown }).calls);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

async function readCount(store: AiBudgetStore, key: string): Promise<number> {
  return callsOf(await store.get(key).catch(() => null));
}

/**
 * Month-to-date (or day-to-date) calls for a tenant: the canonical key plus the
 * legacy keys that hold the rest of the same allowance. See
 * {@link legacyAiBudgetKeys} for why the legacy side is added rather than
 * ignored, and when it can be dropped.
 */
export async function readAiUsed(
  store: AiBudgetStore,
  sub: string,
  window: AiWindow,
  now: Date = new Date(),
): Promise<number> {
  const stamp = periodStamp(window, now);
  const counts = await Promise.all([
    readCount(store, aiBudgetKey(sub, window, stamp)),
    ...legacyAiBudgetKeys(sub, window, stamp).map((k) => readCount(store, k)),
  ]);
  return counts.reduce((a, b) => a + b, 0);
}

/** TTL outlives the period it counts, then expires on its own. No cleanup job. */
function ttlFor(window: AiWindow): number {
  return window === "month" ? 35 * 86_400 : 2 * 86_400;
}

/**
 * Add `cost` to the canonical key.
 *
 * Only the canonical key is ever written, and it holds only its own share of
 * the total. Folding the legacy counts into it would double-charge them on the
 * next read, because the read sums both sides.
 */
async function bumpUsed(
  store: AiBudgetStore,
  sub: string,
  window: AiWindow,
  cost: number,
  now: Date,
): Promise<void> {
  const key = aiBudgetKey(sub, window, periodStamp(window, now));
  const canonical = await readCount(store, key);
  await store
    .put(key, JSON.stringify({ calls: canonical + cost }), { expirationTtl: ttlFor(window) })
    .catch(() => undefined);
}

/** Read the allowance without consuming any of it. Powers the quota endpoint. */
export async function readAiUsage(
  store: AiBudgetStore,
  sub: string,
  tier: AiTier | string,
  now: Date = new Date(),
): Promise<AiUsage> {
  const policy = policyForTier(tier);
  const used = await readAiUsed(store, sub, policy.window, now);
  return usageOf(policy, used, now);
}

function usageOf(policy: AiTierPolicy, used: number, now: Date): AiUsage {
  return {
    used,
    limit: policy.limit,
    remaining: Math.max(0, policy.limit - used),
    window: policy.window,
    resetsAt: resetsAt(policy.window, now),
  };
}

export interface ReserveResult {
  ok: boolean;
  usage: AiUsage;
}

/**
 * Consume `cost` calls from the tenant's allowance before the model runs.
 *
 * `cost` exists because one chat turn and one insight-card batch are not the
 * same amount of Bedrock. Charging both as "1 call" would let the expensive one
 * hide behind the cheap one's price.
 *
 * There is no compare-and-swap here, and KV is eventually consistent, so two
 * simultaneous requests can both read the same count. The overshoot is bounded
 * by concurrency (a handful of calls, cents) and the alternative, a Durable
 * Object per tenant on the hot path of every chat token, costs more than it
 * saves. If that ever stops being true, this is the function to move.
 */
export async function reserveAiCalls(
  store: AiBudgetStore,
  sub: string,
  tier: AiTier | string,
  cost = 1,
  now: Date = new Date(),
): Promise<ReserveResult> {
  const policy = policyForTier(tier);
  const used = await readAiUsed(store, sub, policy.window, now);

  if (used + cost > policy.limit) {
    return { ok: false, usage: usageOf(policy, used, now) };
  }

  await bumpUsed(store, sub, policy.window, cost, now);
  return { ok: true, usage: usageOf(policy, used + cost, now) };
}

/**
 * Charge `cost` calls that have already been allowed, and report the new total.
 *
 * Separate from {@link reserveAiCalls} because carrier_ask on Pro is "always
 * allowed, overage billed", not "refused at the limit". Routing that through
 * `reserveAiCalls` would stop the counter dead on call 1,001: the reservation
 * fails, nothing is written, and every later call reads the same 1,000. The
 * meter would look healthy while Carrier stopped invoicing the overage it was
 * still paying Bedrock for, revenue lost, silently.
 *
 * So this increments unconditionally and can return `used` above `limit`.
 * `remaining` still floors at zero. Whether the caller was allowed to make the
 * call is the caller's decision, taken before this runs.
 */
export async function recordAiCalls(
  store: AiBudgetStore,
  sub: string,
  tier: AiTier,
  cost = 1,
  now: Date = new Date(),
): Promise<AiUsage> {
  const policy = policyForTier(tier);
  const used = await readAiUsed(store, sub, policy.window, now);
  await bumpUsed(store, sub, policy.window, cost, now);
  return usageOf(policy, used + cost, now);
}
